import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ComboBox, Input, useDialog } from '@gokrew/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@gokrew/ui/components/table';
import { Copy, ExternalLink, Presentation, Ban } from 'lucide-react';
import {
  createCrmLiveDemo,
  getCrmLiveDemos,
  revokeCrmLiveDemo,
  type CrmLiveDemo,
  type LiveDemoLanguage,
} from '@/api/crm-live-demo';
import { useCrmFormat } from './crm-format';
import { crmRowInteraction } from './crm-row-interaction';
import { CrmButton, CrmEmpty, CrmError, CrmRequired } from './CrmShared';
import { useLiveDemoCreate } from './crm-live-demo-dialog';
import styles from './Crm.module.css';

const LANGUAGES: LiveDemoLanguage[] = ['fr', 'en'];

const demoStatus = (demo: CrmLiveDemo) =>
  demo.revokedAt
    ? 'revoked'
    : new Date(demo.expiresAt).getTime() < Date.now()
      ? 'expired'
      : 'active';

const demoTitle = (demo: CrmLiveDemo) =>
  [demo.prospectName, demo.companyName].filter(Boolean).join(' · ');

/** One link: copy or open it, what happened in each demo, revoke it. */
const CrmLiveDemoPanel = ({
  demo,
  dialogId,
}: {
  demo: CrmLiveDemo;
  dialogId?: string;
}) => {
  const { t, date } = useCrmFormat();
  const { closeDialog } = useDialog();
  const client = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const status = demoStatus(demo);
  const copy = async () => {
    await navigator.clipboard.writeText(demo.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  const revoke = async () => {
    setBusy(true);
    setError('');
    try {
      await revokeCrmLiveDemo(demo.id);
      await client.invalidateQueries({ queryKey: ['crm', 'live-demos'] });
      if (dialogId) closeDialog(dialogId);
    } catch {
      setError(t('crm.save_error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={styles.form}>
      <div className={styles.demoLink}>
        <Input readOnly value={demo.url} aria-label={t('crm.live_demo.link')} />
        <CrmButton
          variant="outline"
          IconPrefix={Copy}
          onClick={() => void copy()}
        >
          {t(copied ? 'crm.live_demo.copied' : 'crm.live_demo.copy')}
        </CrmButton>
        <CrmButton
          variant="outline"
          IconPrefix={ExternalLink}
          onClick={() => window.open(demo.url, '_blank', 'noopener')}
        >
          {t('crm.live_demo.open')}
        </CrmButton>
      </div>
      <p className={styles.notice}>{t('crm.live_demo.try_hint')}</p>
      <dl className={styles.leadFacts}>
        <div>
          <dt>{t('crm.live_demo.status')}</dt>
          <dd>{t(`crm.live_demo.statuses.${status}`)}</dd>
        </div>
        <div>
          <dt>{t('crm.live_demo.language')}</dt>
          <dd>{t(`crm.live_demo.languages.${demo.language}`)}</dd>
        </div>
        {demo.dealName && (
          <div>
            <dt>{t('crm.workspace.opportunity')}</dt>
            <dd>{demo.dealName}</dd>
          </div>
        )}
        <div>
          <dt>{t('crm.live_demo.expires')}</dt>
          <dd>{date(demo.expiresAt)}</dd>
        </div>
      </dl>
      <h3 className={styles.demoSessionsTitle}>
        {t('crm.live_demo.sessions', { count: demo.sessions.length })}
      </h3>
      {!demo.sessions.length ? (
        <p className={styles.notice}>{t('crm.live_demo.never_opened')}</p>
      ) : (
        <ul className={styles.demoSessions}>
          {demo.sessions.map((session) => (
            <li key={session.startedAt}>
              <strong>
                {date(session.startedAt, true)}
                {session.durationSeconds != null &&
                  ` · ${t('crm.live_demo.minutes', {
                    count: Math.max(
                      1,
                      Math.round(session.durationSeconds / 60)
                    ),
                  })}`}
              </strong>
              {session.questions.length ? (
                <ul>
                  {session.questions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              ) : (
                <span>{t('crm.live_demo.no_questions')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {status === 'active' && (
        <div className={styles.actions}>
          <CrmButton
            type="button"
            variant="destructive"
            IconPrefix={Ban}
            className={styles.editorDelete}
            disabled={busy}
            onClick={() => (confirming ? void revoke() : setConfirming(true))}
          >
            {t(
              confirming
                ? 'crm.live_demo.revoke_confirm'
                : 'crm.live_demo.revoke'
            )}
          </CrmButton>
        </div>
      )}
    </div>
  );
};

/** A new link, for a deal (names filled from it) or for anyone. */
export const CrmLiveDemoEditor = ({
  deal,
  dialogId,
}: {
  deal?: { id: string; name: string };
  dialogId?: string;
}) => {
  const { t } = useCrmFormat();
  const { closeDialog, openDialog } = useDialog();
  const client = useQueryClient();
  const [language, setLanguage] = useState<LiveDemoLanguage>('fr');
  const [prospectName, setProspectName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const demo = await createCrmLiveDemo({
        dealId: deal?.id ?? null,
        language,
        prospectName,
        companyName,
      });
      await client.invalidateQueries({ queryKey: ['crm', 'live-demos'] });
      if (dialogId) closeDialog(dialogId);
      openDialog({
        title: demoTitle(demo) || t('crm.live_demo.title'),
        containerClass: styles.editorDialog,
        content: <CrmLiveDemoPanel demo={demo} />,
      });
    } catch {
      setError(t('crm.save_error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <p className={styles.notice}>{t('crm.live_demo.explain')}</p>
      <fieldset className={styles.grid} disabled={busy}>
        {deal && (
          <p className={styles.fullWidth}>
            {t('crm.live_demo.deal_label')} <strong>{deal.name}</strong>
          </p>
        )}
        <label>
          <span>
            {t('crm.live_demo.language')}
            <CrmRequired />
          </span>
          <ComboBox
            size="sm"
            allowUnselect={false}
            value={language}
            options={LANGUAGES.map((value) => ({
              value,
              label: t(`crm.live_demo.languages.${value}`),
            }))}
            onChange={(value) =>
              value && setLanguage(value as LiveDemoLanguage)
            }
          />
        </label>
        <label>
          {t('crm.live_demo.prospect')}
          <Input
            maxLength={200}
            placeholder={deal ? t('crm.live_demo.from_deal') : ''}
            value={prospectName}
            onChange={(event) => setProspectName(event.target.value)}
          />
        </label>
        <label>
          {t('crm.live_demo.company')}
          <Input
            maxLength={200}
            placeholder={deal ? t('crm.live_demo.from_deal') : ''}
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
          />
        </label>
      </fieldset>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <CrmButton
          type="submit"
          variant="adminPrimary"
          IconPrefix={Presentation}
          disabled={busy}
        >
          {t('crm.live_demo.create')}
        </CrmButton>
      </div>
    </form>
  );
};

/** Every demo link, newest first; a row opens its panel. */
export const CrmLiveDemos = ({ search }: { search: string }) => {
  const { t, date } = useCrmFormat();
  const { openDialog } = useDialog();
  const create = useLiveDemoCreate();
  const demos = useQuery({
    queryKey: ['crm', 'live-demos'],
    queryFn: () => getCrmLiveDemos(),
  });
  const wanted = search.trim().toLowerCase();
  const items = (demos.data ?? []).filter(
    (demo) =>
      !wanted ||
      [demo.prospectName, demo.companyName, demo.dealName ?? '']
        .join(' ')
        .toLowerCase()
        .includes(wanted)
  );
  const open = (demo: CrmLiveDemo) =>
    openDialog({
      title: demoTitle(demo) || t('crm.live_demo.title'),
      containerClass: styles.editorDialog,
      content: <CrmLiveDemoPanel demo={demo} />,
    });
  if (demos.isPending)
    return (
      <p role="status" className={styles.loading}>
        {t('crm.loading')}
      </p>
    );
  if (demos.isError) return <CrmError retry={() => void demos.refetch()} />;
  if (!items.length)
    return (
      <div className={styles.panel}>
        <CrmEmpty
          title={t(wanted ? 'crm.no_results' : 'crm.live_demo.empty_title')}
          description={t('crm.live_demo.explain')}
          action={
            <CrmButton
              variant="adminPrimary"
              IconPrefix={Presentation}
              onClick={() => create()}
            >
              {t('crm.live_demo.new')}
            </CrmButton>
          }
        />
      </div>
    );
  return (
    <div className={styles.tableWrap}>
      <Table>
        <TableHeader>
          <TableRow>
            {[
              'prospect',
              'company',
              'deal',
              'language',
              'opened',
              'status',
              'created',
            ].map((key) => (
              <TableHead key={key}>
                {t(`crm.live_demo.columns.${key}`)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((demo) => (
            <TableRow
              key={demo.id}
              className={styles.clickableRow}
              aria-label={t('crm.live_demo.open_named', {
                name: demoTitle(demo) || demo.url,
              })}
              {...crmRowInteraction(() => open(demo))}
            >
              <TableCell>
                <strong>{demo.prospectName || t('crm.unknown')}</strong>
              </TableCell>
              <TableCell>{demo.companyName || t('crm.unknown')}</TableCell>
              <TableCell>{demo.dealName || t('crm.unknown')}</TableCell>
              <TableCell>
                {t(`crm.live_demo.languages.${demo.language}`)}
              </TableCell>
              <TableCell>
                {demo.sessions.length
                  ? `${demo.sessions.length} · ${date(demo.sessions[0].startedAt, true)}`
                  : t('crm.live_demo.never_opened')}
              </TableCell>
              <TableCell>
                <span className={styles.chip}>
                  {t(`crm.live_demo.statuses.${demoStatus(demo)}`)}
                </span>
              </TableCell>
              <TableCell>{date(demo.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
